// Expression editor for a SOURCE-owned field binding — the contract's closed set of Expr kinds, in plain words.
import { Fragment, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import { diApi } from '../../api';
import type { CatalogueField, Expr, IntegrationLookups, SourceEntity } from '../../types';
import { entityLabel, sourceTables } from '../../components/format';
import { CatalogueFieldPicker, MONO, unwrap } from './SharedParts';

type Kind = Expr['kind'];
type StopExpr = Extract<Expr, { kind: 'stop' }>;
type FieldExpr = Extract<Expr, { kind: 'field' }>;

export const EXPR_KINDS: Array<{ kind: Kind; label: string }> = [
  { kind: 'field', label: 'Source field' },
  { kind: 'stop', label: 'Stop' },
  { kind: 'join', label: 'Lookup in another table' },
  { kind: 'date', label: 'Date' },
  { kind: 'resource', label: 'Match to a resource' },
  { kind: 'user_by_phone', label: 'Driver by phone' },
  { kind: 'const', label: 'Fixed text' },
  { kind: 'template', label: 'Template' }
];

const STOP_WHICH: Array<{ v: StopExpr['which']; l: string }> = [
  { v: 'first_delivery', l: 'First delivery stop' },
  { v: 'first_pickup', l: 'First pickup stop' },
  { v: 'index', l: 'Stop number…' }
];
const STOP_PICK: Array<{ v: StopExpr['pick']; l: string }> = [
  { v: 'company', l: 'Company' },
  { v: 'address', l: 'Address' },
  { v: 'appt_date', l: 'Appointment date' },
  { v: 'appt_time', l: 'Appointment time' },
  { v: 'type', l: 'Stop type' }
];

export const TIMEZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'UTC'];

/** The source field an expression reads first — drives the "Aljex has it on N%" bar. */
export const exprPrimaryField = (e?: Expr | null): string | null => {
  if (!e) return null;
  switch (e.kind) {
    case 'field':
      return e.field || null;
    case 'join':
      return e.keyField || null;
    case 'date':
    case 'resource':
    case 'user_by_phone':
      return exprPrimaryField(e.of);
    default:
      return null;
  }
};

/** Is the expression complete enough to produce a value? */
export const exprComplete = (e?: Expr | null): boolean => {
  if (!e) return false;
  switch (e.kind) {
    case 'field':
      return !!e.field;
    case 'const':
      return e.value !== '';
    case 'template':
      return !!e.template;
    case 'stop':
      return e.which !== 'index' || !!e.index;
    case 'join':
      return !!e.keyField && !!e.pick;
    case 'date':
      return exprComplete(e.of) && !!e.timezone;
    case 'resource':
      return exprComplete(e.of) && !!e.resourceTypeId;
    case 'user_by_phone':
      return exprComplete(e.of) && e.resourceTypeIds.length > 0;
    default:
      return false;
  }
};

/** Switch kinds, carrying the source field over where it makes sense. */
export const convertExpr = (from: Expr | undefined, kind: Kind): Expr => {
  const field = exprPrimaryField(from) || '';
  const inner: Expr =
    from && (from.kind === 'field' || from.kind === 'stop')
      ? from
      : from && (from.kind === 'date' || from.kind === 'resource' || from.kind === 'user_by_phone')
      ? from.of
      : { kind: 'field', field };
  switch (kind) {
    case 'field':
      return { kind: 'field', field };
    case 'const':
      return { kind: 'const', value: '' };
    case 'template':
      return { kind: 'template', template: field ? `{{${field}}}` : '' };
    case 'stop':
      return from?.kind === 'stop' ? from : { kind: 'stop', which: 'first_delivery', pick: 'company' };
    case 'join':
      return { kind: 'join', entity: 'carriers', keyField: field || 'carrier_id', pick: '' };
    case 'date':
      return { kind: 'date', of: inner, timezone: 'America/New_York' };
    case 'resource':
      return { kind: 'resource', of: inner, resourceTypeId: '', matchField: '', normalise: null };
    case 'user_by_phone':
      return { kind: 'user_by_phone', of: inner, resourceTypeIds: [] };
    default:
      return { kind: 'field', field };
  }
};

const stopText = (e: StopExpr) =>
  `${e.which === 'index' ? `Stop ${e.index || '?'}` : STOP_WHICH.find((w) => w.v === e.which)?.l || e.which} · ${(
    STOP_PICK.find((p) => p.v === e.pick)?.l || e.pick
  ).toLowerCase()}`;

/** How an incoming value is compared with the chosen resource field. */
const MATCH_MODES: Array<{ v: '' | 'unpad_numeric_suffix' | 'phone'; l: string; chip: string }> = [
  { v: '', l: 'Same text (ignores case and spaces)', chip: '' },
  { v: 'unpad_numeric_suffix', l: 'Ignore leading zeros (IAD85 = IAD085)', chip: ' · ignore leading zeros' },
  { v: 'phone', l: 'Phone number (last 10 digits)', chip: ' · as phone' }
];

/** The process field being filled: for a RESOURCE field, which resource types it accepts and whether it holds several. */
export interface TargetField {
  type: string;
  resourceTypeUids?: string[];
  multiple?: boolean;
}

/**
 * Resource types a RESOURCE field accepts, its own type first; every type when the field
 * doesn't say — or when none of the types it names is available any more (deleted, or
 * another client's), so the admin is never left with an empty, unfixable choice.
 */
export const acceptedResourceTypes = (lookups: IntegrationLookups, target?: TargetField) => {
  const uids = target?.resourceTypeUids || [];
  if (!uids.length) return lookups.resourceTypes;
  const byUid = new Map(lookups.resourceTypes.map((r) => [r.uid, r]));
  const hit = uids.map((u) => byUid.get(u)).filter((r): r is IntegrationLookups['resourceTypes'][number] => !!r);
  return hit.length ? hit : lookups.resourceTypes;
};

type SummaryChip = { text: string; mono?: boolean; tone: 'field' | 'fn' | 'join' | 'text' };

export const exprChips = (e: Expr | undefined, lookups: IntegrationLookups): SummaryChip[] => {
  if (!e) return [];
  const rtName = (id: string) => lookups.resourceTypes.find((r) => r._id === id || r.uid === id)?.name || 'a resource type';
  switch (e.kind) {
    case 'field':
      return [{ text: e.field || '?', mono: true, tone: 'field' }];
    case 'const':
      return [{ text: `“${e.value}”`, tone: 'text' }];
    case 'template':
      return [{ text: e.template || '?', mono: true, tone: 'text' }];
    case 'stop':
      return [{ text: stopText(e), tone: 'field' }];
    case 'join':
      return [
        { text: e.keyField || '?', mono: true, tone: 'field' },
        { text: `${entityLabel(e.entity)} → ${e.pick || '?'}`, tone: 'join' }
      ];
    case 'date':
      return [...exprChips(e.of, lookups), { text: `Date · ${e.timezone}`, tone: 'fn' }];
    case 'resource':
      return [
        ...exprChips(e.of, lookups),
        {
          text: `Match ${rtName(e.resourceTypeId)}${e.matchField ? ` · ${e.matchField}` : ' · any field'}${
            MATCH_MODES.find((m) => m.v === (e.normalise || ''))?.chip ?? ''
          }`,
          tone: 'fn'
        }
      ];
    case 'user_by_phone':
      return [
        ...exprChips(e.of, lookups),
        { text: `Find person by phone${e.resourceTypeIds.length ? ` · ${e.resourceTypeIds.map(rtName).join(', ')}` : ''}`, tone: 'fn' }
      ];
    default:
      return [];
  }
};

export function ExprChips({ expr, lookups }: { expr: Expr | undefined; lookups: IntegrationLookups }) {
  const chips = exprChips(expr, lookups);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      {chips.map((c, i) => (
        <Fragment key={i}>
          {i > 0 && (
            <Typography component="span" variant="caption" color="text.secondary" aria-hidden>
              →
            </Typography>
          )}
          <Chip
            size="small"
            variant={c.tone === 'field' ? 'outlined' : 'filled'}
            color={c.tone === 'fn' ? 'primary' : c.tone === 'join' ? 'secondary' : 'default'}
            label={c.text}
            sx={c.mono ? { fontFamily: MONO, fontSize: '0.75rem' } : undefined}
          />
        </Fragment>
      ))}
    </Stack>
  );
}

// The tables a source has sent — fetched once per session, for the lookup picker.
const sourceTablesCache = new Map<string, Promise<string[]>>();
function useSourceTables(clientUid: string, sourceUid: string, enabled: boolean): string[] {
  const [tables, setTables] = useState<string[]>([]);
  useEffect(() => {
    if (!enabled || !sourceUid) return undefined;
    let alive = true;
    const key = `${clientUid}|${sourceUid}`;
    let p = sourceTablesCache.get(key);
    if (!p) {
      p = diApi
        .getSources(clientUid)
        .then(unwrap)
        .then((sources) => sourceTables((sources || []).find((s) => s.uid === sourceUid)).map((t) => t.name))
        .catch(() => {
          sourceTablesCache.delete(key);
          return [] as string[];
        });
      sourceTablesCache.set(key, p);
    }
    p.then((t) => alive && setTables(t || []));
    return () => {
      alive = false;
    };
  }, [clientUid, sourceUid, enabled]);
  return tables;
}

// Catalogues of the tables a lookup reads from — fetched once per session.
const entityCatalogueCache = new Map<string, Promise<CatalogueField[]>>();
function useEntityCatalogue(clientUid: string, sourceUid: string, entity: SourceEntity, enabled: boolean) {
  const [fields, setFields] = useState<CatalogueField[]>([]);
  useEffect(() => {
    if (!enabled || !sourceUid) return undefined;
    let alive = true;
    const key = `${clientUid}|${sourceUid}|${entity}`;
    let p = entityCatalogueCache.get(key);
    if (!p) {
      p = diApi
        .getCatalogue(clientUid, sourceUid, entity)
        .then(unwrap)
        .catch(() => {
          entityCatalogueCache.delete(key);
          return [] as CatalogueField[];
        });
      entityCatalogueCache.set(key, p);
    }
    p.then((f) => alive && setFields(f || []));
    return () => {
      alive = false;
    };
  }, [clientUid, sourceUid, entity, enabled]);
  return fields;
}

function StopEditor({ value, onChange }: { value: StopExpr; onChange: (e: StopExpr) => void }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <Select size="small" value={value.which} onChange={(e) => onChange({ ...value, which: e.target.value as StopExpr['which'] })}>
        {STOP_WHICH.map((w) => (
          <MenuItem key={w.v} value={w.v}>
            {w.l}
          </MenuItem>
        ))}
      </Select>
      {value.which === 'index' && (
        <TextField
          size="small"
          type="number"
          label="Stop"
          value={value.index ?? ''}
          onChange={(e) => onChange({ ...value, index: Math.max(1, Math.min(20, Number(e.target.value) || 1)) })}
          inputProps={{ min: 1, max: 20 }}
          sx={{ width: 90 }}
        />
      )}
      <Select size="small" value={value.pick} onChange={(e) => onChange({ ...value, pick: e.target.value as StopExpr['pick'] })}>
        {STOP_PICK.map((p) => (
          <MenuItem key={p.v} value={p.v}>
            {p.l}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
}

/** The `of` of a date / resource / phone expression: a source field, or (optionally) a stop. */
function InnerEditor({
  value,
  onChange,
  catalogue,
  allowStop
}: {
  value: Expr;
  onChange: (e: Expr) => void;
  catalogue: CatalogueField[];
  allowStop: boolean;
}) {
  const isStop = value.kind === 'stop';
  const fieldValue = value.kind === 'field' ? value.field : exprPrimaryField(value) || '';
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      {allowStop && (
        <Select
          size="small"
          value={isStop ? 'stop' : 'field'}
          onChange={(e) =>
            onChange(
              e.target.value === 'stop'
                ? { kind: 'stop', which: 'first_delivery', pick: 'company' }
                : ({ kind: 'field', field: fieldValue } as FieldExpr)
            )
          }
          inputProps={{ 'aria-label': 'Read from' }}
        >
          <MenuItem value="field">Source field</MenuItem>
          <MenuItem value="stop">Stop</MenuItem>
        </Select>
      )}
      {isStop ? (
        <StopEditor value={value as StopExpr} onChange={onChange} />
      ) : (
        <CatalogueFieldPicker catalogue={catalogue} value={fieldValue} onChange={(field) => onChange({ kind: 'field', field })} />
      )}
    </Stack>
  );
}

export function ExprEditor({
  expr,
  onChange,
  catalogue,
  lookups,
  clientUid,
  sourceUid,
  targetField
}: {
  expr: Expr | undefined;
  onChange: (e: Expr) => void;
  catalogue: CatalogueField[];
  lookups: IntegrationLookups;
  clientUid: string;
  sourceUid: string;
  targetField?: TargetField;
}) {
  const kind: Kind = expr?.kind || 'field';
  // A resource match starts on the type the process field itself uses.
  const resourceTypes = acceptedResourceTypes(lookups, targetField);
  useEffect(() => {
    if (expr?.kind === 'resource' && !expr.resourceTypeId && resourceTypes[0]) onChange({ ...expr, resourceTypeId: resourceTypes[0]._id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expr?.kind, expr?.kind === 'resource' && expr.resourceTypeId, resourceTypes[0]?._id]);
  const joinEntity = expr?.kind === 'join' ? expr.entity : 'carriers';
  const entityFields = useEntityCatalogue(clientUid, sourceUid, joinEntity, expr?.kind === 'join');
  // Any table the source has sent can be looked up; the one already chosen stays listed.
  const sentTables = useSourceTables(clientUid, sourceUid, expr?.kind === 'join');
  const joinTables = sentTables.includes(joinEntity) ? sentTables : [joinEntity, ...sentTables];
  const rtOptions = lookups.resourceTypes;

  const label = (text: string) => (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
      {text}
    </Typography>
  );

  let body: JSX.Element | null = null;
  if (!expr || expr.kind === 'field') {
    body = (
      <CatalogueFieldPicker
        catalogue={catalogue}
        value={expr?.kind === 'field' ? expr.field : ''}
        onChange={(field) => onChange({ kind: 'field', field })}
        width={320}
      />
    );
  } else if (expr.kind === 'stop') {
    body = (
      <Box>
        {label('Aljex flattens stops into ps{n} fields; this picks one of them.')}
        <StopEditor value={expr} onChange={onChange} />
      </Box>
    );
  } else if (expr.kind === 'join') {
    body = (
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Select
            size="small"
            value={expr.entity}
            onChange={(e) => onChange({ ...expr, entity: e.target.value, pick: '' })}
            inputProps={{ 'aria-label': 'Look up in' }}
          >
            {joinTables.map((t) => (
              <MenuItem key={t} value={t}>
                {entityLabel(t)}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="body2">by this record&apos;s</Typography>
          <CatalogueFieldPicker
            catalogue={catalogue}
            value={expr.keyField}
            onChange={(keyField) => onChange({ ...expr, keyField })}
            width={220}
          />
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="body2">and use its</Typography>
          <CatalogueFieldPicker
            catalogue={entityFields}
            value={expr.pick}
            onChange={(pick) => onChange({ ...expr, pick })}
            placeholder={expr.entity === 'carriers' ? 'e.g. mc_num' : 'Field'}
            width={220}
          />
        </Stack>
      </Stack>
    );
  } else if (expr.kind === 'date') {
    body = (
      <Stack spacing={1.5}>
        <InnerEditor value={expr.of} onChange={(of) => onChange({ ...expr, of })} catalogue={catalogue} allowStop />
        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="di-tz">Time zone</InputLabel>
            <Select
              labelId="di-tz"
              label="Time zone"
              value={expr.timezone}
              onChange={(e) => onChange({ ...expr, timezone: String(e.target.value) })}
            >
              {TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            Aljex sends dates with no time zone; they are read in this one.
          </Typography>
        </Stack>
      </Stack>
    );
  } else if (expr.kind === 'resource') {
    const chosenType = lookups.resourceTypes.find((r) => r._id === expr.resourceTypeId);
    const typeFields = chosenType?.fields || [];
    // A saved binding may use a type the field doesn't list (the earlier UI offered every type):
    // keep it selectable so the dropdown shows what is actually saved.
    const typeChoices = chosenType && !resourceTypes.some((r) => r._id === chosenType._id) ? [...resourceTypes, chosenType] : resourceTypes;
    const fieldListed = !expr.matchField || typeFields.some((f) => f.name === expr.matchField);
    body = (
      <Stack spacing={1.5}>
        {label('Aljex value')}
        <InnerEditor value={expr.of} onChange={(of) => onChange({ ...expr, of })} catalogue={catalogue} allowStop />
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="di-rt">Resource type</InputLabel>
            <Select
              labelId="di-rt"
              label="Resource type"
              value={typeChoices.some((r) => r._id === expr.resourceTypeId) ? expr.resourceTypeId : ''}
              onChange={(e) => onChange({ ...expr, resourceTypeId: String(e.target.value), matchField: '' })}
            >
              {typeChoices.map((r) => (
                <MenuItem key={r._id} value={r._id}>
                  {r.name}
                  {resourceTypes.some((t) => t._id === r._id) ? '' : ' (not accepted by this field)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={!chosenType}>
            <InputLabel id="di-rf">Match on</InputLabel>
            <Select
              labelId="di-rf"
              label="Match on"
              value={fieldListed ? expr.matchField || '' : expr.matchField}
              onChange={(e) => onChange({ ...expr, matchField: String(e.target.value) })}
            >
              <MenuItem value="">
                <em>Any field</em>
              </MenuItem>
              {typeFields.map((f) => (
                <MenuItem key={f.name} value={f.name}>
                  {f.label || f.name}
                </MenuItem>
              ))}
              {!fieldListed && <MenuItem value={expr.matchField || ''}>{expr.matchField} (no longer on this type)</MenuItem>}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="di-rm">Compare as</InputLabel>
            <Select
              labelId="di-rm"
              label="Compare as"
              value={expr.normalise || ''}
              onChange={(e) => onChange({ ...expr, normalise: (String(e.target.value) || null) as typeof expr.normalise })}
            >
              {MATCH_MODES.map((m) => (
                <MenuItem key={m.v} value={m.v}>
                  {m.l}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {chosenType
            ? `Finds the ${chosenType.name} whose ${expr.matchField || 'fields'} ${expr.matchField ? 'holds' : 'hold'} the Aljex value. `
            : ''}
          A value listing several entries (separated by commas, semicolons or |) adds one resource per entry. If nothing matches, the field
          is left empty.
          {targetField?.multiple === false ? ' This field takes a single resource, so map a value that holds one entry.' : ''}
        </Typography>
      </Stack>
    );
  } else if (expr.kind === 'user_by_phone') {
    body = (
      <Stack spacing={1.5}>
        <InnerEditor value={expr.of} onChange={(of) => onChange({ ...expr, of })} catalogue={catalogue} allowStop={false} />
        <Autocomplete
          multiple
          size="small"
          options={rtOptions.map((r) => r._id)}
          value={expr.resourceTypeIds}
          onChange={(_, v) => onChange({ ...expr, resourceTypeIds: v })}
          getOptionLabel={(id) => rtOptions.find((r) => r._id === id)?.name || id}
          renderInput={(params) => <TextField {...params} label="Search in" placeholder="Resource types, e.g. Drivers" />}
          sx={{ maxWidth: 480 }}
        />
      </Stack>
    );
  } else if (expr.kind === 'const') {
    body = (
      <TextField
        size="small"
        label="Text"
        value={expr.value}
        onChange={(e) => onChange({ ...expr, value: e.target.value })}
        sx={{ maxWidth: 420 }}
      />
    );
  } else if (expr.kind === 'template') {
    body = (
      <TextField
        size="small"
        label="Template"
        value={expr.template}
        onChange={(e) => onChange({ ...expr, template: e.target.value })}
        helperText="Insert source fields with {{field}}, e.g. {{truck_num}}-{{id}}"
        inputProps={{ style: { fontFamily: MONO, fontSize: '0.85rem' } }}
        sx={{ maxWidth: 480 }}
      />
    );
  }

  return (
    <Stack spacing={1.5}>
      <FormControl size="small" sx={{ maxWidth: 280 }}>
        <InputLabel id="di-expr-kind">Value from</InputLabel>
        <Select
          labelId="di-expr-kind"
          label="Value from"
          value={kind}
          onChange={(e) => onChange(convertExpr(expr, e.target.value as Kind))}
        >
          {EXPR_KINDS.map((k) => (
            <MenuItem key={k.kind} value={k.kind}>
              {k.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {body}
    </Stack>
  );
}
